import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Box,
  Typography,
  Avatar,
  Chip,
  Link,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  NewReleasesOutlined as NewIcon,
  AttachMoney as BetIcon,
  Waves as WhaleIcon,
  EmojiObjectsOutlined as SmartMoneyIcon,
  AccountCircle as AccountCircleIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Lightbulb as RelevantIcon,
} from '@mui/icons-material';

const PolymarketStoryCard = ({ story }) => {
  if (!story) return null;

  const { type, market, bet, price_change } = story;

  const renderHeader = () => {
    switch (type) {
      case 'new_market':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <NewIcon sx={{ fontSize: '1.5rem', color: 'success.main' }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              New market
            </Typography>
          </Box>
        );

      case 'relevant_market':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <RelevantIcon sx={{ fontSize: '1.5rem', color: 'info.main' }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Relevant Market
            </Typography>
          </Box>
        );

      case 'bet':
        const tradeAmount = bet?.usdc ;
        const outcome = bet?.outcome || 'Unknown';
        const side = bet?.side || 'Unknown';
        const volume = bet?.user_volume || 0;
        const pnl = bet?.user_pnl || 0;
        const pnl_prct = volume > 0 ? pnl / volume * 100 : 0;
        // Determine user label
        let userLabel = bet?.user_name || bet?.user_pseudonym || bet?.user_id || 'Unknown';
        if (userLabel.length > 10) {
          userLabel = userLabel.substring(0, 10) + '...';
        }
        return (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip
              icon={side === 'BUY' ? <ArrowUpIcon sx={{ color: 'white !important' }} /> : <ArrowDownIcon sx={{ color: 'white !important' }} />}
              label={side}
              size="small"
              sx={{
                height: 24,
                backgroundColor: side === 'BUY' ? 'success.main' : 'error.main',
                color: 'white',
              }}
            />
            <Chip
              icon={<AccountCircleIcon />}
              label={userLabel}
              size="small"
              variant="outlined"
              sx={{ height: 24 }}
            />
            <Chip
              icon={<BetIcon />}
              label={Math.round(tradeAmount).toLocaleString()}
              size="small"
              variant="outlined"
              sx={{ height: 24 }}
            />
            <Chip
              label={outcome}
              size="small"
              variant="outlined"
              sx={{ height: 24 }}
            />
            {volume > 15000 && (
              <Chip
                icon={<WhaleIcon />}
                label="whale"
                size="small"
                variant="outlined"
                sx={{ height: 24 }}
              />
            )}
            {pnl_prct > 20 && (
              <Chip
                icon={<SmartMoneyIcon />}
                label="smart-money"
                size="small"
                variant="outlined"
                sx={{ height: 24 }}
              />
            )}
          </Box>
        );

      case 'price_change':
        const priceChange = market?.one_hour_price_change || 0;
        const isPositive = priceChange >= 0;
        const percentChange = Math.abs(priceChange * 100).toFixed(2);
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            {isPositive ? (
              <TrendingUpIcon sx={{ fontSize: '1.5rem', color: 'success.main' }} />
            ) : (
              <TrendingDownIcon sx={{ fontSize: '1.5rem', color: 'error.main' }} />
            )}
            <Typography
              variant="body2"
              sx={{ color: isPositive ? 'success.main' : 'error.main', fontWeight: 600 }}
            >
              Price Change: {percentChange}%
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  const renderAdditionalText = () => {
    if (type === 'price_change' && price_change) {
      const getLinkDomain = (url) => {
        try {
          const domain = new URL(url).hostname;
          return domain.replace('www.', '');
        } catch {
          return 'Link';
        }
      };

      return (
        <Box sx={{ mt: 2 }}>
          {price_change.comment && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {price_change.comment}
            </Typography>
          )}
          {price_change.link && (
            <Link href={price_change.link} target="_blank" rel="noopener" variant="caption">
              {getLinkDomain(price_change.link)} →
            </Link>
          )}
        </Box>
      );
    }
    return null;
  };

  const renderFooter = () => {
    if (type === 'new_market' && market.start_date) {
      return (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Started on {new Date(market.start_date).toLocaleDateString()}
        </Typography>
      );
    }
    if (type === 'bet' && bet) {
      let betUserLabel = bet?.user_name || bet?.user_pseudonym || bet?.user_id || 'Unknown';
      if (betUserLabel.length > 10) {
        betUserLabel = betUserLabel.substring(0, 10) + '...';
      }
      const action = bet?.side === 'BUY' ? 'bought' : 'sold';
      const betDate = new Date(bet.timestamp).toLocaleDateString();
      return (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          On {betDate}, {betUserLabel} {action} {bet.shares} shares {bet.outcome} at price {Number(bet.price).toFixed(2)}
        </Typography>
      );
    }
    if (type === 'price_change' && price_change?.timestamp) {
      const observedDate = new Date(price_change.timestamp).toLocaleDateString();
      return (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Observed at {observedDate}
        </Typography>
      );
    }
    if (type === 'relevant_market' && market?.start_date && market?.end_date) {
      const startDate = new Date(market.start_date).toLocaleDateString();
      const endDate = new Date(market.end_date).toLocaleDateString();
      return (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Started on {startDate} - Ends on {endDate}
        </Typography>
      );
    }
    return null;
  };

  return (
    <Card sx={{ mb: 2, backgroundColor: 'background.paper' }}>
      <CardContent sx={{ pb: renderFooter() ? 1 : 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          {/* Avatar */}
          <Avatar
            src={market?.icon}
            alt={market?.question}
            sx={{ width: 48, height: 48, flexShrink: 0 }}
          >
            {!market?.icon && market?.question?.[0]}
          </Avatar>

          {/* Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Question as small header */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                mb: 1,
              }}
            >
              {market?.question || 'No question'}
            </Typography>

            {/* Main content - previously the header */}
            {renderHeader()}

            {/* Additional Text */}
            {renderAdditionalText()}
          </Box>
        </Box>
      </CardContent>
      {renderFooter() && (
        <CardActions sx={{ pt: 0, px: 2, pb: 1.5 }}>
          {renderFooter()}
        </CardActions>
      )}
    </Card>
  );
};

export default PolymarketStoryCard;

